import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Grid } from '@mui/system';
import { isEmpty } from 'lodash';
import { CAP_TABLE_CURRENCY_PAGE } from 'common/constants/currencyPageTypes';
import { SSV_REFERENCE_TITLE, SSV_REFERENCE_TYPE } from 'common/constants/documents';
import { VALUATIONS_PAGE_KEY, VALUATIONS_PAGE_VALUE } from 'common/constants/notes';
import { MISSING_SECURITIES, MISSING_SECURITIES_MESSAGE, VALUATIONS_SLUG } from 'common/constants/valuations';
import { useFormat } from 'common/hooks';
import { useStore } from 'common/store';
import { EXPORT_MODE_STYLES } from 'common/types/pdfExport';
import { UseStoreValues } from 'common/types/store';
import { CustomContainer } from 'components';
import { useLayoutContext } from 'context/LayoutContext';
import { useExportSignal } from 'features/pdfExport';
import { EmptyCapTableMessage } from 'pages/CapTable/cap-table/components';
import { PdfExportTargets } from 'pages/Valuations/util/constants';
import { useValuationNotesCache } from 'pages/ValuationsAllocation/hooks';
import { getApproachTableName } from 'pages/ValuationsAllocation/util';
import ValuationContext from 'pages/ValuationsAllocation/ValuationContext';
import { useDocuments } from 'services/hooks';
import { useNotes } from 'services/hooks/notes';
import { CapTableSelection } from './components/CapTableSelection';
import { SecuritySharesTable } from './components/SecurityShares';
import { getRowConfig as securitySharesRowConfig } from './components/SecurityShares/config/rowConfig';
import { SSV_TABLE_NAMES } from './constants';
import { SpecifiedShareValuesProps } from './types';
import WidgetDrawer from '../../../../components/Drawer/widget-drawer/WidgetDrawer';
import { WidgetDrawerApplicationContext } from '../../../../components/Drawer/widget-drawer/WidgetDrawer.props';

const { CAP_TABLE_SELECTION, SECURITY_SHARES } = SSV_TABLE_NAMES;

const VALUATION_SSV_CAP_TABLE_SELECTION_CONTAINER_CLASS = `${VALUATIONS_SLUG}-${SSV_REFERENCE_TYPE}-${CAP_TABLE_SELECTION}-container`;
const VALUATION_SSV_SECURITY_SHARES_CONTAINER_CLASS = `${VALUATIONS_SLUG}-${SSV_REFERENCE_TYPE}-${SECURITY_SHARES}-container`;

const SpecifiedShareValues = ({
  spreadsheets,
  onChange,
  workbook,
  approach,
  approachTableName,
}: SpecifiedShareValuesProps) => {
  const [storeValue] = useStore() as unknown as UseStoreValues;
  const { capTableList } = storeValue;
  const [isSelectedCapTableEmpty, setIsSelectedCapTableEmpty] = useState(false);
  const { measurementDate, filesInApproach, notesInApproach, setFilesInApproach, resetConfiguration, setNotesInApproach, registerApproachFormat } =
    useContext(ValuationContext);
  const [format, formatDispatch] = useFormat({ page: CAP_TABLE_CURRENCY_PAGE });

  const { isExportMode } = useLayoutContext();

  const [isDisplayingRowNumbers, setIsDisplayingRowNumbers] = useState(false);

  const ssvApproachTableName = useMemo(() => {
    if (!approachTableName) {
      return getApproachTableName({ approach });
    }

    return approachTableName;
  }, [approachTableName, approach]);

  useEffect(() => {
    registerApproachFormat?.(ssvApproachTableName, format, formatDispatch);
  }, [ssvApproachTableName, format, formatDispatch, registerApproachFormat]);

  const { capTableSelectionSheet } = spreadsheets;
  const securitySharesSheet = spreadsheets[ssvApproachTableName];
  const { isDisabled } = capTableSelectionSheet.tableData;

  const approachPanelId = approach.panelId;
  const { filesToSave, setFilesToSave } = useDocuments();
  const { notes, setNotes, notesHasChanged, setNotesHasChanged, onAddNote, onUpdateNotes, onDeleteNote } = useNotes();
  const { notes: cachedNotes } = useValuationNotesCache({
    cachedNotes: notesInApproach,
    notes,
    notesHasChanged,
    panelId: approachPanelId,
    setCachedNotes: setNotesInApproach,
    setNotes,
    setNotesHasChanged,
  });

  const onCapTableChange = useCallback(
    (cell, value) => {
      if ((capTableList ?? []).length > 0 && typeof value === 'number') {
        const selectedCapTable = (capTableList ?? []).find(ct => ct.id === value);
        if (selectedCapTable?.securities) {
          setIsSelectedCapTableEmpty(selectedCapTable.securities.length === 0);
          securitySharesSheet.reset({
            tableData: {
              capTable: selectedCapTable,
              securities: selectedCapTable.securities,
            },
            rowConfig: securitySharesRowConfig({
              securities: selectedCapTable.securities,
              isDisabled,
            }),
            columns: null,
          });
          capTableSelectionSheet.reset({
            tableData: null,
            rowConfig: null,
            columns: [
              [],
              {
                ...approach.valuations_approach_ssv,
                cap_table: value,
                share_values: selectedCapTable?.securities,
              },
            ],
          });
          onChange(cell, value);
          if (resetConfiguration) {
            resetConfiguration();
          }
        }
      }
    },
    [
      capTableList,
      onChange,
      resetConfiguration,
      approach.valuations_approach_ssv,
      capTableSelectionSheet,
      securitySharesSheet,
      isDisabled,
    ]
  );

  useEffect(() => {
    if (capTableList) {
      const selectedCapTable = capTableList.find(ct => ct.is_primary);
      if (selectedCapTable?.securities) {
        setIsSelectedCapTableEmpty(selectedCapTable.securities.length === 0);
      }
    }
  }, [capTableList]);

  useEffect(() => {
    const tmpFilesInApproach = filesInApproach || [];
    if (tmpFilesInApproach.length > 0) {
      const tmpFilesToSave = tmpFilesInApproach.filter(file => file.panelId === approachPanelId);
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore setFilesToSave is not typed
      setFilesToSave(tmpFilesToSave[0]?.filesToSave);
    }
  }, [approachPanelId, filesInApproach, filesToSave, setFilesToSave]);

  useEffect(() => {
    if (setFilesInApproach && filesToSave?.length > 0) {
      setFilesInApproach(prevFiles => [
        {
          ...prevFiles,
          filesToSave,
          panelId: approachPanelId,
        },
      ]);
    }
  }, [approachPanelId, filesToSave, setFilesInApproach]);

  let ssvExportStatus: 'loading' | 'missing' | 'ready' = 'ready';
  if (!securitySharesSheet?.data) {
    ssvExportStatus = 'loading';
  } else if (isSelectedCapTableEmpty) {
    ssvExportStatus = 'missing';
  }
  useExportSignal({
    target: PdfExportTargets.SPECIFIED_SHARE_VALUES.id,
    status: ssvExportStatus,
  });

  return (
    <CustomContainer>
      {/* Cap table selection */}
      <Grid className={VALUATION_SSV_CAP_TABLE_SELECTION_CONTAINER_CLASS} size={{ xs: 12 }}>
        <CapTableSelection spreadsheets={spreadsheets} onChange={onCapTableChange} workbook={workbook} />
      </Grid>

      {/* Security shares */}
      {isSelectedCapTableEmpty && ssvApproachTableName.length > 0 ? (
        <Grid size={{ xs: 12 }}>
          <EmptyCapTableMessage title={MISSING_SECURITIES} tagline={MISSING_SECURITIES_MESSAGE} />
        </Grid>
      ) : (
        // Security shares
        <Grid className={VALUATION_SSV_SECURITY_SHARES_CONTAINER_CLASS} size={{ xs: 12 }} data-ref={PdfExportTargets.SPECIFIED_SHARE_VALUES.id} style={isExportMode ? EXPORT_MODE_STYLES : {}}>
          <SecuritySharesTable
            spreadsheet={spreadsheets[ssvApproachTableName]}
            onChange={onChange}
            workbook={workbook}
            setIsDisplayingRowNumbers={setIsDisplayingRowNumbers}
            format={format}
            formatDispatch={formatDispatch}
          />

          <WidgetDrawer
            applicationContext={WidgetDrawerApplicationContext.SSV}
            notesProps={{
              pageType: VALUATIONS_PAGE_VALUE,
              pageTypeKey: VALUATIONS_PAGE_KEY,
              pageTypeId: approach.id,
              notes: cachedNotes,
              isApproach: true,
              setNotes,
              onAddNote,
              onUpdateNotes,
              onDeleteNote,
              isDisabled,
            }}
            documentReferencesProps={{
              selectedMeasurementDate: measurementDate,
              referenceType: SSV_REFERENCE_TYPE,
              currentPage: SSV_REFERENCE_TITLE,
              referencedFeatureId: approach.id,
              filesToSave,
              setFilesToSave,
              isDisplayingRowNumbers,
              isDisabled,
            }}
          />
        </Grid>
      )}
    </CustomContainer>
  );
};

export default SpecifiedShareValues;
